/**
 * Two-step password recovery for salon-admin / staff / super-admin.
 * Same component reused for /forgot-password and /reset-password routes.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate, useParams } from "react-router-dom";
import { Loader2, CheckCircle2, ArrowLeft, KeyRound, Mail } from "lucide-react";
import api from "@/lib/api";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setErr("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Fehler – bitte erneut versuchen.");
    } finally { setSubmitting(false); }
  };

  return (
    <Shell title="Passwort vergessen">
      {done ? (
        <SuccessCard
          title="Mail unterwegs"
          message="Falls die E-Mail registriert ist, haben wir dir einen Reset-Link gesendet. Prüfe auch deinen Spam-Ordner."
        />
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <p className="text-sm opacity-75">
            Gib die E-Mail-Adresse ein, mit der du dich registriert hast. Wir senden dir einen Link, um ein neues Passwort zu setzen.
          </p>
          <div>
            <label className="block text-xs font-medium mb-1.5">E-Mail</label>
            <input
              required type="email"
              className="mv-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="forgot-email"
              autoFocus
            />
          </div>
          {err && <p className="text-sm text-red-600">{err}</p>}
          <button data-testid="forgot-submit" disabled={submitting} className="mv-btn w-full justify-center">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            Reset-Link senden
          </button>
          <Link to="/login" className="flex items-center gap-1.5 text-xs justify-center opacity-70 hover:opacity-100">
            <ArrowLeft className="w-3 h-3" /> Zurück zum Login
          </Link>
        </form>
      )}
    </Shell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { slug } = useParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { if (!token) setErr("Kein Token im Link gefunden."); }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) { setErr("Mindestens 8 Zeichen."); return; }
    if (pw !== pw2) { setErr("Passwörter stimmen nicht überein."); return; }
    setSubmitting(true);
    try {
      const endpoint = slug
        ? `/public/salon/${slug}/customer/reset-password`
        : "/auth/reset-password";
      await api.post(endpoint, { token, password: pw });
      setDone(true);
      setTimeout(() => navigate(slug ? `/salon/${slug}/account` : "/login"), 2200);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Fehler – bitte erneut versuchen.");
    } finally { setSubmitting(false); }
  };

  return (
    <Shell title="Neues Passwort">
      {done ? (
        <SuccessCard title="Passwort aktualisiert" message="Du wirst gleich weitergeleitet…" />
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <p className="text-sm opacity-75">Wähle ein neues Passwort. Es muss mindestens 8 Zeichen lang sein.</p>
          <div>
            <label className="block text-xs font-medium mb-1.5">Neues Passwort</label>
            <input required type="password" className="mv-input" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password" autoFocus />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5">Passwort wiederholen</label>
            <input required type="password" className="mv-input" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-password-confirm" />
          </div>
          {err && <p className="text-sm text-red-600" data-testid="reset-error">{err}</p>}
          <button data-testid="reset-submit" disabled={submitting || !token} className="mv-btn w-full justify-center">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            Passwort aktualisieren
          </button>
          <Link to="/login" className="flex items-center gap-1.5 text-xs justify-center opacity-70 hover:opacity-100">
            <ArrowLeft className="w-3 h-3" /> Zurück zum Login
          </Link>
        </form>
      )}
    </Shell>
  );
}

function Shell({ title, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--mv-bg)" }}>
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <p className="mv-label">StudioMV</p>
          <h1 className="font-display text-4xl mt-2">{title}</h1>
        </div>
        <div className="mv-card p-8">{children}</div>
      </div>
    </div>
  );
}

function SuccessCard({ title, message }) {
  return (
    <div className="text-center" data-testid="reset-success">
      <CheckCircle2 className="w-12 h-12 mx-auto mb-3" strokeWidth={1.5} style={{ color: "#10B981" }} />
      <h3 className="font-display text-2xl mb-2">{title}</h3>
      <p className="text-sm opacity-75 mb-4">{message}</p>
      <Link to="/login" className="mv-btn-ghost text-sm inline-flex">
        <ArrowLeft className="w-3.5 h-3.5" /> Zum Login
      </Link>
    </div>
  );
}
